import AdminCombobox from 'components/comboboxes/AdminCombobox';
import MutedText from 'components/common/MutedText';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertInput from 'components/HubertUI/HubertInput';
import HubertLabel from 'components/HubertUI/HubertLabel';
import { ChevronDown, ChevronRight, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { useCreateClientMutation } from 'rtk/query/apis/clients';
import type { TCreateClientContract } from 'rtk/query/apis/clients';
import { toggleCreateClientDialog } from 'rtk/layout/LayoutControlSlice';
import { useAppDispatch } from 'rtk/query/hooks';

const schema = z.object({
  orgName: z.string().min(2, 'Name must be at least 2 characters.'),
  customerSuccessManagerId: z.string().min(1, 'Responsible CS is required.'),
});

const contractSchema = z
  .object({
    startDate: z.string().min(1, 'Start date is required.'),
    endDate: z.string().min(1, 'End date is required.'),
    contractSignerFirstName: z.string().trim().min(1, 'First name is required.'),
    contractSignerLastName: z.string().trim().min(1, 'Last name is required.'),
    interviews: z.coerce.number().int().min(1, 'Interviews must be at least 1.'),
    interviewsConsumedWhen: z.enum(['engaged', 'started', 'created']),
    isPilot: z.boolean(),
    pilotEndDate: z.string().optional(),
  })
  .refine((d) => !d.startDate || !d.endDate || d.startDate < d.endDate, {
    message: 'End date must be after the start date.',
    path: ['endDate'],
  })
  .refine((d) => !d.isPilot || !!d.pilotEndDate, {
    message: 'Pilot end date is required.',
    path: ['pilotEndDate'],
  })
  .refine(
    (d) =>
      !d.isPilot ||
      !d.pilotEndDate ||
      !d.endDate ||
      !d.startDate ||
      (d.startDate <= d.pilotEndDate && d.pilotEndDate <= d.endDate),
    {
      message: 'Pilot end date must be on or after the contract start date and on or before the contract end date.',
      path: ['pilotEndDate'],
    },
  );

type ContractForm = {
  startDate: string;
  endDate: string;
  interviews: string;
  interviewsConsumedWhen: 'engaged' | 'started' | 'created';
  contractSignerFirstName: string;
  contractSignerLastName: string;
  isPilot: boolean;
  pilotEndDate: string;
  pilotEndSameAsContract: boolean;
};

const oneYearFrom = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(year + 1, month - 1, day)).toISOString().slice(0, 10);
};

const emptyContract: ContractForm = {
  startDate: '',
  endDate: '',
  interviews: '',
  interviewsConsumedWhen: 'engaged',
  contractSignerFirstName: '',
  contractSignerLastName: '',
  isPilot: false,
  pilotEndDate: '',
  pilotEndSameAsContract: false,
};

const CreateClient = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createClient, { isLoading }] = useCreateClientMutation();

  const [form, setForm] = useState({ orgName: '', customerSuccessManagerId: '' });
  const [errors, setErrors] = useState<{ orgName?: string; customerSuccessManagerId?: string }>({});
  const [showContract, setShowContract] = useState(false);
  const [contractForm, setContractForm] = useState<ContractForm>(emptyContract);
  const [contractErrors, setContractErrors] = useState<Partial<Record<keyof ContractForm, string>>>({});

  const validate = () => {
    const result = schema.safeParse(form);
    if (!result.success) {
      const fieldErrors: { orgName?: string; customerSuccessManagerId?: string } = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as string;
        if (key === 'orgName') fieldErrors.orgName = issue.message;
        if (key === 'customerSuccessManagerId') fieldErrors.customerSuccessManagerId = issue.message;
      }
      setErrors(fieldErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const validateContract = (): TCreateClientContract | null | false => {
    if (!showContract) return null;
    const result = contractSchema.safeParse({
      startDate: contractForm.startDate,
      endDate: contractForm.endDate,
      contractSignerFirstName: contractForm.contractSignerFirstName,
      contractSignerLastName: contractForm.contractSignerLastName,
      interviews: contractForm.interviews,
      interviewsConsumedWhen: contractForm.interviewsConsumedWhen,
      isPilot: contractForm.isPilot,
      pilotEndDate: contractForm.pilotEndSameAsContract ? contractForm.endDate : contractForm.pilotEndDate,
    });
    if (!result.success) {
      const errs: typeof contractErrors = {};
      for (const issue of result.error.issues) {
        errs[issue.path[0] as keyof ContractForm] = issue.message;
      }
      setContractErrors(errs);
      return false;
    }
    setContractErrors({});
    const pilotEndDate = contractForm.isPilot
      ? contractForm.pilotEndSameAsContract
        ? contractForm.endDate
        : contractForm.pilotEndDate
      : undefined;
    return {
      startDate: contractForm.startDate,
      endDate: result.data.endDate,
      contractSigner: {
        firstName: result.data.contractSignerFirstName,
        lastName: result.data.contractSignerLastName,
      },
      quotas: { interviews: result.data.interviews, interviewsConsumedWhen: result.data.interviewsConsumedWhen },
      ...(pilotEndDate ? { pilot: { endDate: pilotEndDate } } : {}),
    };
  };

  const handleSubmit = async () => {
    const isValid = validate();
    const contract = validateContract();
    if (!isValid || contract === false) return;
    try {
      const { client } = await createClient({
        orgName: form.orgName,
        customerSuccessManagerId: form.customerSuccessManagerId,
        ...(contract ? { contract } : {}),
      }).unwrap();

      dispatch(toggleCreateClientDialog({ show: false }));
      toast.success(`Client "${form.orgName}" created successfully.`);
      setTimeout(() => {
        navigate(`/clients/${(client as any).id ?? (client as any)._id}`);
      }, 500);
    } catch (err: any) {
      if (err?.status === 409) {
        const message = err?.data ?? 'Could not create client.';
        setErrors((p) => ({ ...p, orgName: message }));
      } else {
        const message = err?.data?.message ?? err?.message ?? 'Something went wrong while creating the client.';
        toast.error(message);
      }
    }
  };

  const isSubmitDisabled = !form.orgName.trim() || !form.customerSuccessManagerId || isLoading;

  return (
    <div>
      <div className="mb-6">
        <div className="text-xl font-semibold tracking-tight">Create Client</div>
        <MutedText text="Fill in the organisation details to create a new client." />
      </div>

      <div className="space-y-4">
        <div className="space-y-1">
          <HubertLabel htmlFor="orgName">
            Organisation name <span className="text-destructive">*</span>
          </HubertLabel>
          <HubertInput
            id="orgName"
            value={form.orgName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setForm((p) => ({ ...p, orgName: e.target.value }));
              setErrors((p) => ({ ...p, orgName: undefined }));
            }}
            placeholder="Acme Corp"
          />
          {errors.orgName && <p className="text-destructive text-xs">{errors.orgName}</p>}
        </div>

        <div className="space-y-1">
          <HubertLabel>
            Responsible CS <span className="text-destructive">*</span>
          </HubertLabel>
          <AdminCombobox
            value={form.customerSuccessManagerId}
            onValueChange={(val) => {
              setForm((p) => ({ ...p, customerSuccessManagerId: val as string }));
              setErrors((p) => ({ ...p, customerSuccessManagerId: undefined }));
            }}
          />
          {errors.customerSuccessManagerId && (
            <p className="text-destructive text-xs">{errors.customerSuccessManagerId}</p>
          )}
        </div>

        {/* Contract details — optional */}
        <div className="border-t pt-4">
          <button
            type="button"
            className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
            onClick={() => setShowContract((v) => !v)}
          >
            {showContract ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Contract details <span className="font-normal text-gray-400">(optional)</span>
          </button>

          {showContract && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <HubertLabel htmlFor="startDate">
                    Start date <span className="text-destructive">*</span>
                  </HubertLabel>
                  <HubertInput
                    id="startDate"
                    type="date"
                    value={contractForm.startDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const startDate = e.target.value;
                      setContractForm((p) => ({
                        ...p,
                        startDate,
                        endDate: p.endDate || (startDate ? oneYearFrom(startDate) : p.endDate),
                      }));
                      setContractErrors((p) => ({ ...p, startDate: undefined }));
                    }}
                  />
                  {contractErrors.startDate && <p className="text-destructive text-xs">{contractErrors.startDate}</p>}
                </div>
                <div className="space-y-1">
                  <HubertLabel htmlFor="endDate">
                    End date <span className="text-destructive">*</span>
                  </HubertLabel>
                  <HubertInput
                    id="endDate"
                    type="date"
                    value={contractForm.endDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setContractForm((p) => ({ ...p, endDate: e.target.value }));
                      setContractErrors((p) => ({ ...p, endDate: undefined }));
                    }}
                  />
                  {contractErrors.endDate && <p className="text-destructive text-xs">{contractErrors.endDate}</p>}
                </div>
              </div>

              <div>
                <MutedText className="mb-2 text-xs">
                  Contract signer <span className="text-destructive">*</span>
                </MutedText>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <HubertLabel htmlFor="contractSignerFirstName">First name</HubertLabel>
                    <HubertInput
                      id="contractSignerFirstName"
                      value={contractForm.contractSignerFirstName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setContractForm((p) => ({ ...p, contractSignerFirstName: e.target.value }))
                      }
                      placeholder="Jane"
                    />
                    {contractErrors.contractSignerFirstName && (
                      <p className="text-destructive text-xs">{contractErrors.contractSignerFirstName}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <HubertLabel htmlFor="contractSignerLastName">Last name</HubertLabel>
                    <HubertInput
                      id="contractSignerLastName"
                      value={contractForm.contractSignerLastName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setContractForm((p) => ({ ...p, contractSignerLastName: e.target.value }))
                      }
                      placeholder="Doe"
                    />
                    {contractErrors.contractSignerLastName && (
                      <p className="text-destructive text-xs">{contractErrors.contractSignerLastName}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <HubertLabel htmlFor="quotaInterviews">
                    Interviews <span className="text-destructive">*</span>
                  </HubertLabel>
                  <HubertInput
                    id="quotaInterviews"
                    type="number"
                    min={1}
                    value={contractForm.interviews}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setContractForm((p) => ({ ...p, interviews: e.target.value }))
                    }
                    placeholder="e.g. 1500"
                  />
                  {contractErrors.interviews && <p className="text-destructive text-xs">{contractErrors.interviews}</p>}
                </div>

                <div className="space-y-1">
                  <HubertLabel htmlFor="interviewsConsumedWhen">Consumed when</HubertLabel>
                  <select
                    id="interviewsConsumedWhen"
                    value={contractForm.interviewsConsumedWhen}
                    onChange={(e) =>
                      setContractForm((p) => ({
                        ...p,
                        interviewsConsumedWhen: e.target.value as ContractForm['interviewsConsumedWhen'],
                      }))
                    }
                    className="border-input bg-background h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm"
                  >
                    <option value="engaged">Engaged</option>
                    <option value="started">Started</option>
                    <option value="created">Created</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={contractForm.isPilot}
                    onChange={(e) =>
                      setContractForm((p) => ({
                        ...p,
                        isPilot: e.target.checked,
                        pilotEndDate: '',
                        pilotEndSameAsContract: false,
                      }))
                    }
                  />
                  This is a pilot contract
                </label>

                {contractForm.isPilot && (
                  <div className="ml-6 space-y-2">
                    <div className="space-y-1">
                      <HubertLabel htmlFor="pilotEndDate">Pilot end date</HubertLabel>
                      <HubertInput
                        id="pilotEndDate"
                        type="date"
                        value={contractForm.pilotEndSameAsContract ? contractForm.endDate : contractForm.pilotEndDate}
                        disabled={contractForm.pilotEndSameAsContract}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setContractForm((p) => ({ ...p, pilotEndDate: e.target.value }));
                          setContractErrors((p) => ({ ...p, pilotEndDate: undefined }));
                        }}
                      />
                      {contractErrors.pilotEndDate && (
                        <p className="text-destructive text-xs">{contractErrors.pilotEndDate}</p>
                      )}
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={contractForm.pilotEndSameAsContract}
                        onChange={(e) =>
                          setContractForm((p) => ({
                            ...p,
                            pilotEndSameAsContract: e.target.checked,
                            pilotEndDate: '',
                          }))
                        }
                      />
                      Same as contract end date
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bottom-0 mt-6 flex w-full flex-col items-end">
        <div className="flex items-center justify-end gap-3">
          <HubertButton
            variant="secondary"
            size="sm"
            onClick={() => {
              dispatch(toggleCreateClientDialog({ show: false }));
            }}
          >
            Cancel
          </HubertButton>

          <HubertButton disabled={isSubmitDisabled} size="sm" onClick={handleSubmit}>
            {isLoading ? <RotateCw className="mr-2 animate-spin" /> : null}
            Create
          </HubertButton>
        </div>
      </div>
    </div>
  );
};

export default CreateClient;
